import {lazyload, accessibility, responsive, placeholder} from '@cloudinary/react';

/**
 * Constructs an array with the plugins to apply.
 *
 * To use `lazyload` plugin, a `useLazyLoad` key should be set to true.
 * Margin and threshold can be customized with the `lazyLoadMargin` and `lazyLoadThreshold` keys.
 *
 * `responsive` plugin will be added if there are steps defined in the `responsiveSteps` key.
 *
 * The `accessibility` plugin will be added if the `useAccessibility` key is set to true. The mode can be configured with the `accessibilityMode`  key.
 *
 * `placeholder` plugin will be added based on the `usePlaceholder` key, and the mode will be controlled by the `placeholderMode` value.
 *
 * There's an extra field, `extraPlugins` that allows to add custom plugins to the plugins array.
 *
 * @param {Object} options - The configuration options for constructing the plugins.
 * @param {boolean} options.useLazyLoad - Indicates whether lazy loading should be enabled.
 * @param {number} options.lazyLoadThreshold - The threshold for lazy loading.
 * @param {number} options.lazyLoadMargin - The margin for lazy loading.
 * @param {Array} options.responsiveSteps - The responsive steps for the plugin.
 * @param {boolean} options.usePlaceholder - Indicates whether placeholder should be used.
 * @param {string} options.placeholderMode - The mode for the placeholder.
 * @param {boolean} options.useAccessibility - Indicates whether accessibility should be enabled.
 * @param {string} options.accessibilityMode - The mode for accessibility.
 * @param {Array} options.extraPlugins - Additional plugins to be included.
 * @returns {Array} - An array of plugins based on the provided configuration options.
 */
const constructPlugins = ({
  useLazyLoad,
  lazyLoadThreshold,
  lazyLoadMargin,
  responsiveSteps,
  usePlaceholder,
  placeholderMode,
  useAccessibility,
  accessibilityMode,
  extraPlugins,
}) => {
  const plugins = [];
  if (useLazyLoad) {
    plugins.push(lazyload({rootMargin: lazyLoadMargin, threshold: lazyLoadThreshold}));
  }
  if (responsiveSteps && (!Array.isArray(responsiveSteps) || responsiveSteps?.length)) {
    plugins.push(responsive({steps: responsiveSteps}));
  }
  if (useAccessibility) {
    plugins.push(accessibility({mode: accessibilityMode}));
  }
  if (usePlaceholder) {
    plugins.push(placeholder({mode: placeholderMode}));
  }
  if (extraPlugins && extraPlugins?.length > 0) {
    plugins.push(...extraPlugins);
  }

  return plugins;
};

export {constructPlugins};
